import React from 'react';

/**
 * Button component that implements our application's gradient styling
 * 
 * @param {Object} props - Component props
 * @param {string} [props.variant='primary'] - Button variant (primary, secondary, outline)
 * @param {string} [props.size='md'] - Button size (sm, md, lg)
 * @param {boolean} [props.isLoading=false] - Loading state
 * @param {boolean} [props.fullWidth=false] - Whether button should take full width
 * @param {React.ReactNode} props.children - Button content
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} rest - Any other props to pass to the button element
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  children,
  className = '',
  ...rest
}) => {
  // Base button classes
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-150';
  
  // Gradient primary variant - our blue gradient
  const primaryClasses = `bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white 
                         hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-800 dark:hover:to-indigo-800
                         focus:ring-blue-500 dark:focus:ring-blue-600`;
  
  // Secondary variant (solid but not gradient)
  const secondaryClasses = 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 focus:ring-slate-400 dark:focus:ring-slate-500';
  
  // Outline variant                      
  const outlineClasses = 'bg-transparent border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:ring-slate-400 dark:focus:ring-slate-500';
  
  // Size variations
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base'
  };
  
  // Disabled or loading state
  const isDisabled = isLoading || disabled;
  const disabledClasses = isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  
  // Width
  const widthClasses = fullWidth ? 'w-full' : '';
  
  // Get variant classes (disable hover effects when disabled)
  const variantClasses = 
    variant === 'primary' ? (isDisabled ? 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white' : primaryClasses) : 
    variant === 'secondary' ? (isDisabled ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200' : secondaryClasses) : 
    variant === 'outline' ? (isDisabled ? 'bg-transparent border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300' : outlineClasses) : 
    (isDisabled ? 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white' : primaryClasses);
  
  // Combine all classes
  const buttonClasses = `${baseClasses} ${variantClasses} ${sizeClasses[size]} ${disabledClasses} ${widthClasses} ${className}`;
  
  return (
    <button 
      className={buttonClasses}
      disabled={isDisabled}
      {...rest}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button; 